import React, { useState } from 'react';
import { Link } from 'react-router-dom';

export default function WhatWeDo() {
  const [activeCategory, setActiveCategory] = useState('all');

  const experiences = [
    {
      id: 'great-west-africa-rendezvous',
      title: 'The Great West Africa Rendezvous (GWR)',
      tagline: 'Large-scale cultural convergence across West Africa',
      description: 'A large-scale cultural and destination experience designed to bring West Africa together through a neutral meeting point.',
      category: 'cultural-immersion',
      color: 'from-amber-500 to-orange-600',
      stats: [
        { label: 'Countries', value: '8+' },
        { label: 'Annual Scale', value: '5,000+' },
        { label: 'IP Ownership', value: '100% MGL' }
      ]
    },
    {
      id: 'boundless-europe-experience',
      title: 'Boundless Europe Experience',
      tagline: 'Multi-country European travel for diaspora & groups',
      description: 'A destination-led travel programme that spans multiple European countries, designed for group travel, diaspora engagement, and cultural immersion.',
      category: 'diaspora-legacy',
      color: 'from-blue-500 to-indigo-600',
      stats: [
        { label: 'Countries', value: '4-6' },
        { label: 'Duration', value: '10-14 days' },
        { label: 'Group Size', value: '20-40' }
      ]
    }
  ];

  const experienceCategories = [
    { id: 'all', name: 'All Formats' },
    { id: 'roots-reconnection', name: 'Roots & Reconnection Experiences' },
    { id: 'cultural-immersion', name: 'Cultural Immersion Experiences' },
    { id: 'custom-organisational', name: 'Custom Organisational Experiences' },
    { id: 'wellness-renewal', name: 'Wellness & Renewal Experiences' },
    { id: 'diaspora-legacy', name: 'Diaspora & Legacy Journeys' },
    { id: 'themed-signature', name: 'Themed Signature Journeys' }
  ];

  const deliveryMethods = [
    {
      title: 'Licensable Travel Experiences',
      description: 'Proven experience formats that travel operators can license and deliver with consistency across markets.',
    },
    {
      title: 'White-Label Experience Formats',
      description: 'Ready-to-run experiences operators can sell under their own brand, supported by clear systems and operational playbooks.',
    },
    {
      title: 'Custom Travel & Experience Design',
      description: 'End-to-end design for brands, corporates, and organisations seeking bespoke destination experiences.',
    },
    {
      title: 'Sponsorship & Partnership Structuring',
      description: 'Strategic partnerships and integrations that enhance the experience itself—focused on alignment and long-term value, not logo placement.',
    }
  ];

  const targetAudience = [
    'Travel operators and tour companies',
    'Brands and corporate organisations',
    'Cultural institutions and communities',
    'Strategic partners and sponsors'
  ];

  const filteredExperiences = activeCategory === 'all' 
    ? experiences 
    : experiences.filter(exp => exp.category === activeCategory);

  return (
    <div className="bg-gray-50">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-secondary/20"></div>
        </div>
        <div className="section-container relative">
          <div className="max-w-4xl">
            <div className="inline-flex items-center space-x-2 mb-6">
              <div className="w-3 h-3 bg-primary rounded-full"></div>
              <span className="text-sm font-medium text-primary uppercase tracking-wider">
                Scalable Destination Experiences
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              What We Do
            </h1>
            <p className="text-2xl md:text-3xl font-semibold mb-6 text-gray-100">
              Destination Experiences, Built to Scale
            </p>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl">
              We design, own, and deploy destination-led travel experiences that can be repeated, 
              adapted, and delivered across markets, without losing meaning or quality.
            </p>
          </div>
        </div>
      </section>

      {/* What We Create Section */}
      <section className="section-container py-16">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">What We Create</h2>
          <p className="text-gray-600">
            Structured experience formats designed for consistency and scalability
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {experienceCategories.slice(1).map((category) => (
            <div 
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`bg-white rounded-xl shadow-lg p-6 cursor-pointer transition-all hover:shadow-xl ${
                activeCategory === category.id 
                  ? 'ring-2 ring-primary transform -translate-y-1' 
                  : 'hover:transform hover:-translate-y-1'
              }`}
            >
              <div className="flex items-start mb-4">
                <div className="text-2xl mr-3">●</div>
                <h3 className="text-xl font-bold text-gray-900">{category.name}</h3>
              </div>
              <p className="text-gray-600">
                {category.id === 'roots-reconnection' && 'Journeys centred on heritage, identity, and cultural reconnection.'}
                {category.id === 'cultural-immersion' && 'Locally rooted experiences shaped by people, tradition, and place.'}
                {category.id === 'custom-organisational' && 'Purpose-built retreats and programmes for teams and organisations.'}
                {category.id === 'wellness-renewal' && 'Intentional journeys focused on rest, clarity, and restoration.'}
                {category.id === 'diaspora-legacy' && 'Experiences exploring lineage, memory, and generational legacy.'}
                {category.id === 'themed-signature' && 'Curated, repeatable journeys built around a clear theme.'}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Experience Showcase */}
      <section className="bg-white py-16">
        <div className="section-container">
          <div className="flex flex-wrap gap-2 mb-8">
            {experienceCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeCategory === category.id
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>

          <div className="space-y-12">
            {filteredExperiences.map((exp) => (
              <div key={exp.id} className="bg-gray-50 rounded-2xl overflow-hidden shadow-xl">
                <div className="md:flex">
                  {/* Left side - Content */}
                  <div className="md:w-2/3 p-8 md:p-12">
                    <div className="inline-flex items-center mb-4">
                      <div className={`w-4 h-4 bg-gradient-to-r ${exp.color} rounded-full mr-2`}></div>
                      <span className="text-sm font-medium text-primary uppercase tracking-wider">
                        Licensable IP Format
                      </span>
                    </div>
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                      {exp.title}
                    </h3>
                    <p className="text-lg text-gray-600 mb-6">
                      {exp.description}
                    </p>
                    
                    <div className="flex flex-wrap gap-4 mb-6">
                      {exp.stats.map((stat, idx) => (
                        <div key={idx} className="bg-white rounded-lg px-4 py-3 shadow-sm">
                          <div className="text-xl font-bold text-gray-900">{stat.value}</div>
                          <div className="text-sm text-gray-500">{stat.label}</div>
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-wrap gap-4">
                      <Link
                        to={`/experiences`}
                        className="inline-flex items-center px-6 py-3 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 transition-colors"
                      >
                        View Format Details
                        <svg className="w-5 h-5 ml-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </Link>
                      <Link
                        to="/contact"
                        className="inline-flex items-center px-6 py-3 border-2 border-gray-900 text-gray-900 font-medium rounded-lg hover:bg-gray-900 hover:text-white transition-colors"
                      >
                        Discuss Licensing
                      </Link>
                    </div>
                  </div>

                  {/* Right side - Visual */}
                  <div className={`md:w-1/3 bg-gradient-to-br ${exp.color} p-8 md:p-12 flex items-center justify-center`}>
                    <div className="text-center">
                      <div className="text-5xl mb-4">🌍</div>
                      <p className="text-white font-semibold text-lg">
                        Available for Licensing
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Deliver Section */}
      <section className="section-container py-16">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">How We Deliver</h2>
          <p className="text-xl text-gray-600">
            Our experiences are designed as structured formats, not one-off trips—allowing them to be 
            deployed, adapted, and scaled.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {deliveryMethods.map((method, index) => (
            <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
              <div className="text-4xl mb-4">{method.icon}</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{method.title}</h3>
              <p className="text-gray-600">{method.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Who It's For & Outcome Section */}
      <div className="bg-gray-900 text-white">
        <div className="section-container py-16">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Who It's For */}
            <div>
              <h2 className="text-3xl font-bold mb-8">Who It's For</h2>
              <div className="space-y-4">
                {targetAudience.map((audience, index) => (
                  <div key={index} className="flex items-start">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></div>
                    <span className="text-lg text-gray-300">{audience}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* The Outcome */}
            <div>
              <h2 className="text-3xl font-bold mb-8">The Outcome</h2>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8">
                <p className="text-xl">
                  Experiences that feel intentional, travel well across markets, and perform commercially, 
                  without sacrificing authenticity, quality, or purpose.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <section className="section-container py-20 text-center">
        <div className="max-w-2xl mx-auto">
         
          <p className="text-xl text-gray-600 mb-8">
            Partner with us to license proven formats or create custom experiences that deliver consistent quality across markets.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="btn-outline border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white"
            >
              Join the Experience
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
